import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { listChannelInstances } from '@/services/channelsApi'
import { getApiBaseUrl } from '@/config/env'

type WidgetPosition = 'bottom-right' | 'bottom-left'

export default function WebChatSetup() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [color, setColor] = useState('#2563eb')
  const [welcome, setWelcome] = useState('¡Hola! ¿En qué podemos ayudarte?')
  const [position, setPosition] = useState<WidgetPosition>('bottom-right')

  const { data: instances = [], isLoading } = useQuery({
    queryKey: ['channel-instances'],
    queryFn: listChannelInstances,
  })

  const instance = useMemo(() => instances.find((item) => item.id === id), [instances, id])
  const widgetToken = String(instance?.config?.widgetToken || '')
  const tenantAttr = String(instance?.tenantId || '')

  const scriptSrc = `${window.location.origin}/widget.js`
  const apiBase = getApiBaseUrl()

  const snippet = `<script
  src="${scriptSrc}"
  data-widget-token="${widgetToken}"
  data-tenant="${tenantAttr}"
  data-api="${apiBase}"
  data-color="${color}"
  data-welcome="${welcome.replace(/"/g, '&quot;')}"
  data-position="${position}">
</script>`

  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(snippet)
      toast.success('Snippet copiado al portapapeles')
    } catch {
      toast.error('No se pudo copiar el snippet')
    }
  }

  if (isLoading) {
    return <div className="p-6 text-gray-500">Cargando configuración de WebChat...</div>
  }

  if (!instance || instance.channelType !== 'WEBCHAT') {
    return (
      <div className="p-6">
        <p className="text-red-600 mb-4">Instancia WebChat no encontrada.</p>
        <button
          onClick={() => navigate('/app/instances')}
          className="px-4 py-2 rounded-lg bg-gray-900 text-white"
        >
          Volver a Canales
        </button>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configuración WebChat Widget</h1>
          <p className="text-sm text-gray-600 mt-1">{instance.instanceName}</p>
        </div>
        <button
          onClick={() => navigate('/app/instances')}
          className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          Volver
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
          <h2 className="font-semibold text-gray-900">Personalización</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Color primario</label>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-20 p-1 border border-gray-300 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mensaje de bienvenida</label>
            <input
              type="text"
              value={welcome}
              onChange={(e) => setWelcome(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              maxLength={200}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Posición</label>
            <select
              value={position}
              onChange={(e) => setPosition(e.target.value as WidgetPosition)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="bottom-right">Bottom Right</option>
              <option value="bottom-left">Bottom Left</option>
            </select>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
          <h2 className="font-semibold text-gray-900">Snippet embebible</h2>
          <p className="text-sm text-gray-600">
            Copia este script y pégalo antes del cierre de <code>&lt;/body&gt;</code> de tu sitio web.
          </p>
          <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-auto whitespace-pre-wrap break-all">
            {snippet}
          </pre>
          <button
            onClick={copySnippet}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
          >
            Copiar snippet
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900 mb-3">Preview rápido</h2>
        <div className="relative h-64 rounded-lg border border-dashed border-gray-300 bg-gray-50">
          <div
            className={`absolute ${position === 'bottom-right' ? 'right-4' : 'left-4'} bottom-4 flex flex-col items-end gap-2`}
          >
            <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm shadow-sm max-w-xs">
              {welcome}
            </div>
            <button
              className="w-12 h-12 rounded-full text-white shadow-lg"
              style={{ backgroundColor: color }}
            >
              💬
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
